import { zodResolver } from '@hookform/resolvers/zod';
import { type AdminSelf, isApiError } from '@saay/api-client';
import {
  formatTimestamp,
  isShebaChecksumValid,
  normalizeShebaInput,
  statusLabel,
} from '@saay/formatting';
import {
  applyFieldErrors,
  Banner,
  Button,
  CommandFeedback,
  CountBadge,
  Dialog,
  EmptyState,
  ErrorState,
  FactList,
  FieldErrorList,
  LoadMore,
  Ltr,
  Masked,
  Skeleton,
  StatusChip,
  Table,
  TextArea,
  TextField,
  useToast,
} from '../kit.js';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';

import type {
  AdminMerchant,
  PayoutAccount,
  PayoutAccountChangeRequest,
  PayoutAccountChangeRequestApplication,
  PayoutAccountChangeRequestRejection,
  PayoutAccountRequest,
  PayoutAccountVerification,
} from '../api.js';
import { SurfaceSheetSection } from '../components/surface-section.js';
import { useSubmitBlocked } from '../components/form.js';
import { ShebaField } from '../components/masked-fields.js';
import { OnDemandCopy } from '../components/on-demand-copy.js';
import { PAYOUT_HOLD_FULL_FA, PAYOUT_HOLD_SHORT_FA } from '../ux-copy.js';
import { QueryState } from '../components/guard.js';
import { ReasonDialog } from '../components/reason-dialog.js';
import { approvalSubmitLabel, canUse, executesApprovalDirectly } from '../permissions.js';
import {
  listStatus,
  setupKeys,
  useAdminSelf,
  usePayoutAccounts,
  usePayoutChangeRequests,
} from '../queries.js';
import { reasonSchema } from '../validation.js';
import { useRefreshMerchant } from './merchant-lifecycle.js';
import { FormReasonChips } from '../components/reason-field.js';

/**
 * Payout accounts of a merchant (AC §18.5 P4–P10; D-24, F-2; D-61; FTD §9.3). A manager records a
 * new account (`201`, still `PENDING` verification) and applies a change request (`200 APPLIED`)
 * directly. Anyone else still receives `202`. Verification stays a separate step (P7). The Sheba is
 * masked for everyone but Super and Finance Admin, and every mask is shown with •.
 */

function useRefreshPayout(merchantId: string) {
  const queryClient = useQueryClient();
  const refresh = useRefreshMerchant(merchantId);
  return async (): Promise<void> => {
    await Promise.all([
      refresh(),
      queryClient.invalidateQueries({
        queryKey: [...setupKeys.merchant(merchantId), 'payout-accounts'],
      }),
      queryClient.invalidateQueries({
        queryKey: [...setupKeys.merchant(merchantId), 'payout-change-requests'],
      }),
    ]);
  };
}

function payoutGuidance(error: unknown): ReactNode {
  if (!isApiError(error)) return undefined;
  const details = error.details ?? {};
  switch (error.code) {
    case 'APPROVAL_REQUEST_PENDING':
      return typeof details.approvalRequestId === 'string' ? (
        <>
          درخواست حساب دیگری برای این فروشگاه در انتظار تأیید است.{' '}
          <Link to={`/approvals/${details.approvalRequestId}`}>مشاهدهٔ درخواست</Link>
        </>
      ) : (
        'درخواست حساب دیگری برای این فروشگاه در انتظار تأیید است.'
      );
    case 'PAYOUT_ACCOUNT_UNCHANGED':
      return 'این شبا همان حساب فعال فعلی است.';
    case 'STATE_CONFLICT':
      return typeof details.currentStatus === 'string'
        ? `وضعیت فعلی: ${statusLabel('payoutAccountVerificationStatus', details.currentStatus)}؛ اطلاعات به‌روز شد.`
        : 'وضعیت تغییر کرده است؛ اطلاعات به‌روز شد.';
    case 'RESOURCE_VERSION_CONFLICT':
      return 'این مورد در این فاصله تغییر کرده است؛ اطلاعات به‌روز شد.';
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------------------------
// P6 requestPayoutAccount

const requestSchema = z.object({
  holderName: z.string().trim().min(1, 'نام صاحب حساب الزامی است.').max(200, 'حداکثر ۲۰۰ کاراکتر.'),
  reason: reasonSchema(),
  sheba: z
    .string()
    .transform(normalizeShebaInput)
    .refine(
      (value) => isShebaChecksumValid(value),
      'شبا را به شکل IR و ۲۴ رقم وارد کنید؛ رقم کنترل آن درست نیست.',
    ),
});
type RequestInput = z.input<typeof requestSchema>;
type RequestValues = z.output<typeof requestSchema>;

function RequestAccountDialog({
  merchant,
  onCreated,
  onOpenChange,
}: {
  readonly merchant: AdminMerchant;
  readonly onCreated?: (account: PayoutAccount) => void;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const refresh = useRefreshPayout(merchant.id);
  const toast = useToast();
  const self = useAdminSelf().data;
  const direct = executesApprovalDirectly(self ?? {});
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<RequestInput, unknown, RequestValues>({
    defaultValues: { holderName: merchant.legalNameFa, reason: '', sheba: '' },
    resolver: zodResolver(requestSchema),
  });
  const command = useCommand<PayoutAccountRequest, PayoutAccount>(
    'payout-account.request',
    (client, body, intent) => client.post('/admin/payout-accounts', { body, intent }),
    {
      onApprovalPending: () => refresh(),
      onSuccess: async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          title: 'حساب تسویه ثبت شد',
        });
        onOpenChange(false);
        if (result.data.verificationStatus === 'PENDING') {
          onCreated?.(result.data);
        }
      },
    },
  );

  async function handle(outcome: CommandOutcome<PayoutAccount>): Promise<void> {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    setUnmapped(applyFieldErrors(outcome.error, ['sheba', 'holderName', 'reason'], form.setError));
    if (
      outcome.error.code === 'APPROVAL_REQUEST_PENDING' ||
      outcome.error.code === 'STATE_CONFLICT'
    ) {
      await refresh();
    }
  }

  const error = command.state.status === 'error' ? command.state.error : undefined;
  const line = error === undefined ? undefined : payoutGuidance(error);
  const approvalId = command.state.approval?.approvalRequestId;

  return (
    <Dialog
      eyebrow={merchant.displayNameFa}
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title="درخواست حساب تسویه"
    >
      <form
        className="admin-form"
        noValidate
        onSubmit={form.handleSubmit(async (values) => {
          setUnmapped([]);
          await handle(
            await command.submit({
              holderName: values.holderName.trim(),
              ownerId: merchant.id,
              ownerType: 'MERCHANT',
              reason: values.reason.trim(),
              sheba: values.sheba,
            }),
          );
        })}
      >
        <p className="admin-muted">
          <OnDemandCopy details={PAYOUT_HOLD_FULL_FA} short={PAYOUT_HOLD_SHORT_FA} />
        </p>
        <Controller
          control={form.control}
          name="sheba"
          render={({ field, fieldState }) => (
            <ShebaField
              error={fieldState.error?.message}
              label="شماره شبا"
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />
        <TextField
          error={form.formState.errors.holderName?.message}
          label="نام صاحب حساب"
          {...form.register('holderName')}
        />
        <TextArea
          error={form.formState.errors.reason?.message}
          label="دلیل"
          maxLength={1000}
          {...form.register('reason')}
        />
        <FormReasonChips form={form} intent="merchant." />
        <FieldErrorList errors={unmapped} />
        <CommandFeedback
          approvalAction={
            approvalId === undefined ? undefined : (
              <Link to={`/approvals/${approvalId}`}>مشاهدهٔ درخواست تأیید</Link>
            )
          }
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={command.state}
        />
        {line === undefined ? null : <Banner tone="warning">{line}</Banner>}
        {command.state.status === 'approval-pending' ? (
          <Button onClick={() => onOpenChange(false)} type="button" variant="secondary">
            بستن
          </Button>
        ) : (
          <Button
            disabled={offline.blocked}
            loading={command.state.status === 'pending'}
            type="submit"
          >
            {approvalSubmitLabel(self ?? {})}
          </Button>
        )}
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// P7 verifyPayoutAccount

const verifySchema = z.object({
  evidenceReference: z
    .string()
    .trim()
    .min(1, 'مدرک یا شمارهٔ استعلام را بنویسید.')
    .max(200, 'حداکثر ۲۰۰ کاراکتر.'),
  note: reasonSchema(false),
  result: z.enum(['VERIFIED', 'REJECTED']),
});
type VerifyValues = z.infer<typeof verifySchema>;

function VerifyDialog({
  account,
  merchant,
  onOpenChange,
}: {
  readonly account: PayoutAccount;
  readonly merchant: AdminMerchant;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshPayout(merchant.id);
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<VerifyValues>({
    defaultValues: { evidenceReference: '', note: '', result: 'VERIFIED' },
    resolver: zodResolver(verifySchema),
  });
  const command = useCommand<PayoutAccountVerification, PayoutAccount>(
    'payout-account.verify',
    (client, body, intent) =>
      client.post(`/admin/payout-accounts/${encodeURIComponent(account.id)}/verification`, {
        body,
        intent,
      }),
    {
      onSuccess: async (answer) => {
        await refresh();
        toast.success({
          confirmedBy: answer,
          title:
            answer.data.verificationStatus === 'VERIFIED'
              ? 'حساب تسویه تأیید و فعال شد'
              : 'حساب تسویه رد شد',
        });
        onOpenChange(false);
      },
    },
  );

  async function handle(outcome: CommandOutcome<PayoutAccount>): Promise<void> {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    setUnmapped(
      applyFieldErrors(outcome.error, ['evidenceReference', 'note', 'result'], form.setError),
    );
    if (
      outcome.error.code === 'STATE_CONFLICT' ||
      outcome.error.code === 'RESOURCE_VERSION_CONFLICT'
    ) {
      await refresh();
    }
  }

  const error = command.state.status === 'error' ? command.state.error : undefined;
  const line = error === undefined ? undefined : payoutGuidance(error);

  async function submitVerification(result: VerifyValues['result']) {
    const values = form.getValues();
    setUnmapped([]);
    await handle(
      await command.submit({
        evidenceReference: values.evidenceReference.trim(),
        expectedVersion: account.version,
        method: 'MANUAL',
        result,
        ...(values.note.trim() === '' ? {} : { note: values.note.trim() }),
      }),
    );
  }

  return (
    <Dialog
      eyebrow={merchant.displayNameFa}
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title="تأیید مالکیت حساب تسویه"
    >
      <form className="admin-form" noValidate onSubmit={(event) => event.preventDefault()}>
        <FactList
          items={[
            {
              key: 'sheba',
              label: 'شبا',
              // Super and Finance Admin receive the full Sheba to compare with the bank letter.
              value:
                account.sheba === undefined ? (
                  <Masked kind="sheba" value={account.shebaMasked} />
                ) : (
                  <Ltr>{account.sheba}</Ltr>
                ),
            },
            { key: 'holder', label: 'صاحب حساب', value: account.holderName },
          ]}
        />
        <TextField
          error={form.formState.errors.evidenceReference?.message}
          hint="نامهٔ بانک یا شمارهٔ استعلام"
          label="مدرک"
          {...form.register('evidenceReference')}
        />
        <TextArea
          error={form.formState.errors.note?.message}
          label="یادداشت (اختیاری)"
          maxLength={1000}
          {...form.register('note')}
        />
        <FieldErrorList errors={unmapped} />
        <CommandFeedback
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={command.state}
        />
        {line === undefined ? null : <Banner tone="warning">{line}</Banner>}
        <div className="admin-actions">
          <Button
            disabled={offline.blocked}
            loading={command.state.status === 'pending'}
            onClick={() => void form.handleSubmit(() => submitVerification('VERIFIED'))()}
            type="button"
          >
            تأیید مالکیت
          </Button>
          <Button
            disabled={offline.blocked}
            loading={command.state.status === 'pending'}
            onClick={() => void form.handleSubmit(() => submitVerification('REJECTED'))()}
            type="button"
            variant="danger"
          >
            رد
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// P8–P10 change requests

type ChangeDecision = {
  readonly kind: 'apply' | 'reject';
  readonly request: PayoutAccountChangeRequest;
};

function ChangeRequests({
  merchant,
  self,
}: {
  readonly merchant: AdminMerchant;
  readonly self: AdminSelf;
}) {
  const toast = useToast();
  const refresh = useRefreshPayout(merchant.id);
  const requests = usePayoutChangeRequests(merchant.id);
  const rows = requests.data?.pages.flatMap((page) => page.items) ?? [];
  const [decision, setDecision] = useState<ChangeDecision | null>(null);
  const canApply = canUse(self, 'applyPayoutAccountChangeRequest');
  const canReject = canUse(self, 'rejectPayoutAccountChangeRequest');
  return (
    <>
      <QueryState
        empty={<p className="admin-muted">فروشگاه درخواست تغییر حسابی ثبت نکرده است.</p>}
        error={requests.error}
        isEmpty={rows.length === 0}
        onRetry={() => void requests.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری درخواست‌ها…" lines={3} />}
        status={listStatus(requests)}
      >
        <Table
          caption="درخواست‌های تغییر حساب فروشگاه"
          columns={[
            {
              header: 'شبای پیشنهادی',
              key: 'sheba',
              pin: 'start',
              render: (row) => (
                <span className="saay-cell-main">
                  <Masked kind="sheba" value={row.shebaMasked} />
                  <small>{row.holderName}</small>
                </span>
              ),
            },
            {
              header: 'درخواست‌کننده',
              key: 'by',
              render: (row) => (
                <span className="saay-cell-main">
                  <span>{row.requestedBy.displayNameFa ?? 'بدون نام'}</span>
                  <small>
                    <time dateTime={row.requestedAt}>{formatTimestamp(row.requestedAt)}</time>
                  </small>
                </span>
              ),
            },
            { header: 'دلیل', key: 'reason', render: (row) => row.reason },
            {
              header: 'وضعیت',
              key: 'status',
              render: (row) => (
                <span className="admin-inline">
                  <StatusChip enumName="payoutChangeRequestStatus" value={row.status} />
                  {row.approvalRequestId === undefined || row.approvalRequestId === null ? null : (
                    <Link to={`/approvals/${row.approvalRequestId}`}>تأیید</Link>
                  )}
                </span>
              ),
            },
            {
              detail: false,
              header: <span className="saay-visually-hidden">بررسی</span>,
              key: 'actions',
              pin: 'end',
              render: (row) =>
                row.status !== 'PENDING' ? null : (
                  <span className="admin-inline">
                    {canApply ? (
                      <Button
                        onClick={() => setDecision({ kind: 'apply', request: row })}
                        size="sm"
                      >
                        {approvalSubmitLabel(self)}
                      </Button>
                    ) : null}
                    {canReject ? (
                      <Button
                        onClick={() => setDecision({ kind: 'reject', request: row })}
                        size="sm"
                        variant="ghost"
                      >
                        رد
                      </Button>
                    ) : null}
                  </span>
                ),
            },
          ]}
          regionLabel="جدول درخواست‌های تغییر حساب"
          rowKey={(row) => row.id}
          rows={rows}
        />
        <LoadMore
          hasMore={requests.hasNextPage}
          loading={requests.isFetchingNextPage}
          onLoadMore={() => void requests.fetchNextPage()}
        />
      </QueryState>
      {decision === null ? null : (
        <ReasonDialog<unknown>
          description={
            decision.kind === 'apply'
              ? executesApprovalDirectly(self)
                ? 'حساب پیشنهادی بلافاصله اعمال می‌شود و سپس مالکیت آن بررسی می‌شود.'
                : 'حساب پیشنهادی فروشگاه برای تأیید یک مدیر دیگر ارسال می‌شود؛ پس از تأیید، مالکیت آن بررسی می‌شود.'
              : 'درخواست فروشگاه رد می‌شود و حساب فعلی بدون تغییر می‌ماند.'
          }
          eyebrow={merchant.displayNameFa}
          guidance={payoutGuidance}
          name={`payout-change.${decision.kind}`}
          onDone={async (result, approval) => {
            await refresh();
            if (approval !== undefined) return;
            toast.success({
              confirmedBy: result,
              title:
                decision.kind === 'apply'
                  ? 'درخواست تغییر حساب اعمال شد'
                  : 'درخواست تغییر حساب رد شد',
            });
            setDecision(null);
          }}
          onError={async (error) => {
            if (isApiError(error) && error.status === 409) await refresh();
          }}
          onOpenChange={(open) => {
            if (!open) setDecision(null);
          }}
          send={(client, reason, intent) =>
            client.post(
              `/admin/payout-account-change-requests/${encodeURIComponent(decision.request.id)}/${decision.kind === 'apply' ? 'application' : 'rejection'}`,
              {
                body: {
                  expectedVersion: decision.request.version,
                  reason,
                } satisfies PayoutAccountChangeRequestApplication &
                  PayoutAccountChangeRequestRejection,
                intent,
              },
            )
          }
          submitLabel={decision.kind === 'apply' ? approvalSubmitLabel(self) : 'رد درخواست'}
          title={decision.kind === 'apply' ? 'اعمال درخواست تغییر حساب' : 'رد درخواست تغییر حساب'}
          variant={decision.kind === 'apply' ? 'primary' : 'danger'}
        >
          <FactList
            items={[
              {
                key: 'sheba',
                label: 'شبای پیشنهادی',
                value:
                  decision.request.sheba === undefined ? (
                    <Masked kind="sheba" value={decision.request.shebaMasked} />
                  ) : (
                    <Ltr>{decision.request.sheba}</Ltr>
                  ),
              },
              { key: 'holder', label: 'صاحب حساب', value: decision.request.holderName },
              { key: 'reason', label: 'دلیل فروشگاه', value: decision.request.reason },
            ]}
          />
        </ReasonDialog>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// The section

export function PayoutSection({
  merchant,
  self,
}: {
  readonly merchant: AdminMerchant;
  readonly self: AdminSelf;
}) {
  const readable = canUse(self, 'listPayoutAccounts');
  const accounts = usePayoutAccounts(merchant.id, readable);
  const rows = accounts.data?.pages.flatMap((page) => page.items) ?? [];
  const [open, setOpen] = useState<
    { kind: 'request' } | { kind: 'verify'; account: PayoutAccount } | null
  >(null);
  if (!readable) {
    return null;
  }
  const pendingPayout = merchant.pendingApprovals.some(
    (approval) => approval.actionType === 'PAYOUT_ACCOUNT_CHANGE',
  );
  const canRequest =
    merchant.status !== 'CLOSED' && !pendingPayout && canUse(self, 'requestPayoutAccount');
  const canVerify = canUse(self, 'verifyPayoutAccount');
  const close = (next: boolean) => {
    if (!next) setOpen(null);
  };
  return (
    <>
      <SurfaceSheetSection
        actions={
          canRequest ? (
            <Button onClick={() => setOpen({ kind: 'request' })} size="sm" variant="secondary">
              حساب جدید
            </Button>
          ) : undefined
        }
        id="merchant-payout-accounts"
        title="حساب‌های تسویه"
      >
        {pendingPayout ? (
          <Banner tone="info">
            درخواست حساب تسویهٔ این فروشگاه در انتظار تأیید است؛ پس از تأیید، حساب برای بررسی مالکیت
            در این فهرست دیده می‌شود.
          </Banner>
        ) : null}
        <QueryState
          empty={<EmptyState icon="bank" title="هنوز حسابی ثبت نشده است" />}
          error={accounts.error}
          isEmpty={rows.length === 0}
          onRetry={() => void accounts.refetch()}
          skeleton={<Skeleton label="در حال بارگذاری حساب‌ها…" lines={3} />}
          status={listStatus(accounts)}
        >
          <Table
            caption="حساب‌های تسویهٔ فروشگاه"
            columns={[
              {
                detail: false,
                header: 'شبا',
                key: 'sheba',
                pin: 'start',
                render: (row) => (
                  <span className="saay-cell-main">
                    <Masked kind="sheba" value={row.shebaMasked} />
                    <small>{row.holderName}</small>
                  </span>
                ),
              },
              {
                header: 'اعتبار',
                key: 'validity',
                render: (row) =>
                  row.validFrom === null ? (
                    '—'
                  ) : (
                    <span className="saay-cell-main">
                      <time dateTime={row.validFrom}>{formatTimestamp(row.validFrom)}</time>
                      {row.validTo === null ? null : (
                        <small>
                          <time dateTime={row.validTo}>{`تا ${formatTimestamp(row.validTo)}`}</time>
                        </small>
                      )}
                    </span>
                  ),
              },
              {
                header: 'مدرک',
                key: 'verification',
                render: (row) =>
                  row.verification === null ? '—' : (row.verification.evidenceReference ?? '—'),
              },
              {
                header: 'بررسی‌کننده',
                key: 'decidedBy',
                render: (row) => row.verification?.decidedBy?.displayNameFa ?? '—',
              },
              {
                detail: false,
                header: 'وضعیت',
                key: 'status',
                render: (row) => (
                  <span className="admin-inline">
                    <StatusChip
                      enumName="payoutAccountVerificationStatus"
                      value={row.verificationStatus}
                    />
                    {row.active ? <CountBadge tone="success">حساب فعال</CountBadge> : null}
                  </span>
                ),
              },
              {
                detail: false,
                header: <span className="saay-visually-hidden">بررسی</span>,
                key: 'actions',
                pin: 'end',
                render: (row) =>
                  row.verificationStatus === 'PENDING' && canVerify ? (
                    <Button onClick={() => setOpen({ account: row, kind: 'verify' })} size="sm">
                      بررسی مالکیت
                    </Button>
                  ) : null,
              },
            ]}
            recordDetails={{
              label: (row) => `جزئیات حساب ${row.holderName}`,
              title: (row) => row.holderName,
            }}
            regionLabel="جدول حساب‌های تسویه"
            rowKey={(row) => row.id}
            rows={rows}
          />
          {accounts.isFetchNextPageError ? (
            <ErrorState error={accounts.error} onRetry={() => void accounts.fetchNextPage()} />
          ) : null}
          <LoadMore
            hasMore={accounts.hasNextPage}
            loading={accounts.isFetchingNextPage}
            onLoadMore={() => void accounts.fetchNextPage()}
          />
        </QueryState>
      </SurfaceSheetSection>
      {canUse(self, 'listPayoutAccountChangeRequests') ? (
        <SurfaceSheetSection id="merchant-payout-changes" title="درخواست‌های تغییر حساب از فروشگاه">
          <ChangeRequests merchant={merchant} self={self} />
        </SurfaceSheetSection>
      ) : null}
      {open?.kind === 'request' ? (
        <RequestAccountDialog
          merchant={merchant}
          onCreated={(account) => {
            if (canVerify) setOpen({ account, kind: 'verify' });
          }}
          onOpenChange={close}
        />
      ) : null}
      {open?.kind === 'verify' ? (
        <VerifyDialog account={open.account} merchant={merchant} onOpenChange={close} />
      ) : null}
    </>
  );
}
